import { parseNumber } from '~/utils';

/**
 * Converts a string value to a float number, handling undefined and invalid inputs
 * @param value - The string value to parse
 * @returns The parsed float number or undefined if the input is invalid or empty
 */
export const parseNumericValueToFloat = (value: string | undefined) => {
  if (!value) {
    return undefined;
  }

  const parsed = parseNumber(value);

  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Updates a numeric field in the target object.
 * If `hasMeasure` is `true`, the field represents a `ValueMeasure` object, which consists of `{measure: string, value: number}`.
 * If the parsed value is invalid or `undefined`, the entire field is removed.
 * If `hasMeasure` is `true` and the parsed value is invalid, the entire `ValueMeasure` object is removed to ensure there is no measure without a valid value.
 * @param formValue - The value from the form data to be parsed
 * @param hasMeasure - Indicates whether the field has an associated measure
 * @param target - The object where the field should be updated
 * @param fieldName - The name of the field to update in the target object
 */
export const updateNumericField = (
  formValue: any,
  hasMeasure: boolean,
  target: any,
  fieldName: string,
) => {
  const parsedValue = parseNumericValueToFloat(
    hasMeasure ? formValue?.value : formValue,
  );

  if (Number.isNaN(parsedValue) || parsedValue === undefined) {
    target[fieldName] = undefined; // Remove the entire field if the value is invalid
    return;
  }

  target[fieldName] = hasMeasure
    ? {
        value: parsedValue,
        measure: formValue?.measure,
      }
    : parsedValue;
};

/**
 * Sets a value in a nested object based on a path array
 * @param object - The target object to modify
 * @param path - Array of keys representing the path to the target property
 * @param value - The value to set at the specified path
 * @template T - Type of the object being modified
 */
export const setNestedValue = <T extends Record<string, any>>(
  object: T,
  path: string[],
  value: unknown,
) => {
  let current = object;

  for (const [index, key] of path.entries()) {
    if (index === path.length - 1) {
      current[key] = value;
    } else {
      current = current[key] as Record<string, any>;
    }
  }
};
